import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import "./map.css";
import image from "./assets/map-raaga.png";
import logoRight from "./assets/project-logo.png";
import contactBtn from "./assets/contact.png";
import LocationLabels from "./LocationLabels";
import MapAreas, { plotCoordinates } from "./MapAreas";
import parkImage from "./assets/6.jpg";
import parkImage2 from "./assets/parkiimage.jpg";
import compass from "./assets/compass.png";
import Logo from "./assets/logo.png";

const ZOOM_KEY = "mapZoom";
const POSITION_KEY = "mapPosition";
const COLOR_MODE_KEY = "mapColorMode";
const STATUS_FILTER_KEY = "mapStatusFilter";
const FACING_FILTER_KEY = "mapFacingFilter";

const EDGE_SAFE_PAD = 80;
const DRAG_THRESHOLD = 5;

const normalizeStatus = (status) => String(status || "").trim().toLowerCase();

const normalizeFacing = (facing) => {
  const value = String(facing || "").trim().toUpperCase();

  if (!value || value === "N/A" || value === "TBD") return "TBD";

  const cleaned = value
    .replace(/\s+/g, "")
    .replace(/AND/g, "&")
    .replace(/\+/g, "&");

  // Single directions (full word or abbreviation)
  if (cleaned === "EAST" || cleaned === "E") return "East";
  if (cleaned === "WEST" || cleaned === "W") return "West";
  if (cleaned === "NORTH" || cleaned === "N") return "North";
  if (cleaned === "SOUTH" || cleaned === "S") return "South";

  // Two directions (abbreviations)
  if (cleaned === "E&S" || cleaned === "S&E") return "East+South";
  if (cleaned === "E&N" || cleaned === "N&E") return "East+North";
  if (cleaned === "W&S" || cleaned === "S&W") return "West+South";
  if (cleaned === "W&N" || cleaned === "N&W") return "West+North";
  if (cleaned === "E&W" || cleaned === "W&E") return "East&West";
  if (cleaned === "N&S" || cleaned === "S&N") return "North&South";

  // Two directions (full words, kept for safety)
  if (cleaned === "EAST&SOUTH" || cleaned === "SOUTH&EAST") return "East+South";
  if (cleaned === "EAST&NORTH" || cleaned === "NORTH&EAST") return "East+North";
  if (cleaned === "WEST&SOUTH" || cleaned === "SOUTH&WEST") return "West+South";
  if (cleaned === "WEST&NORTH" || cleaned === "NORTH&WEST") return "West+North";
  if (cleaned === "EAST&WEST" || cleaned === "WEST&EAST") return "East&West";
  if (cleaned === "NORTH&SOUTH" || cleaned === "SOUTH&NORTH") return "North&South";

  return "TBD";
};

const coordsToSvgPoints = (coordsString = "") => {
  const nums = String(coordsString)
    .split(",")
    .map((n) => Number(n.trim()))
    .filter((n) => !Number.isNaN(n));

  const pairs = [];
  for (let i = 0; i < nums.length; i += 2) {
    if (nums[i] != null && nums[i + 1] != null) {
      pairs.push(`${nums[i]},${nums[i + 1]}`);
    }
  }
  return pairs.join(" ");
};

const STATUS_COLORS = {
  available: "rgba(4, 117, 45, 0.78)",
  booked: "rgba(245, 158, 11,  0.78)",
  sold: "rgba(239, 68, 68, 0.78)",
  blocked: "rgba(107, 114, 128, 0.78)",
  unknown: "rgba(107, 114, 128, 0.78)",
};

const FACING_COLORS = {
  East: "rgba(169, 85, 247, 0.77)",
  West: "rgba(20, 184, 165, 0.69)",
  North: "rgba(59, 130, 246, 0.62)",
  South: "rgba(249, 115, 22, 0.62)",
  "East+South": "rgba(236, 72, 153, 0.62)",
  "East+North": "rgba(99, 102, 241, 0.62)",
  "West+South": "rgba(234, 179, 8, 0.62)",
  "West+North": "rgba(14, 164, 233, 0.72)",
  "East&West": "rgba(129, 141, 248, 0.70)",
  "North&South": "rgba(244, 114, 182, 0.62)",
  TBD: "rgba(255, 255, 255, 0.45)",
};

const Map = ({ plots }) => {
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const animationFrameRef = useRef(null);
  const targetZoomRef = useRef(0);
  const targetPositionRef = useRef({ x: 0, y: 0 });

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [availableUnits, setAvailableUnits] = useState("Loading...");
  const [newsItems, setNewsItems] = useState([]);
  const [error, setError] = useState(null);

  const [showInfoTabC5, setShowInfoTabC5] = useState(false);
  const [showInfoTabC7, setShowInfoTabC7] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [touchDistance, setTouchDistance] = useState(null);
  const [hoveredPlotId, setHoveredPlotId] = useState(null);
  const [selectedPlotId, setSelectedPlotId] = useState(null);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [dragDistance, setDragDistance] = useState(0);
  const [wasDragged, setWasDragged] = useState(false);

  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
  const [baseSize, setBaseSize] = useState({ width: 0, height: 0 });

  const getInitialColorMode = useCallback(() => {
    return sessionStorage.getItem(COLOR_MODE_KEY) || "none";
  }, []);

  const getInitialStatusFilter = useCallback(() => {
    return sessionStorage.getItem(STATUS_FILTER_KEY) || "ALL";
  }, []);

  const getInitialFacingFilter = useCallback(() => {
    return sessionStorage.getItem(FACING_FILTER_KEY) || "ALL";
  }, []);

  const [plotMetaMap, setPlotMetaMap] = useState({});
  const [colorMode, setColorMode] = useState(getInitialColorMode);
  const [statusFilter, setStatusFilter] = useState(getInitialStatusFilter);
  const [facingFilter, setFacingFilter] = useState(getInitialFacingFilter);

  const coords = useMemo(
    () => ({
      c5: { x: 2689, y: 1522 },
      c7: { x: 1994, y: 1179 },
      c18: { x: 2712, y: 2366 },
      project6BaseCoords: { x: 2238, y: 6576 },
      project7BaseCoords: { x: 1027, y: 1366 },
    }),
    []
  );

  const minZoom = windowWidth <= 768 ? 2.1 : 3.5;
  const maxZoom = windowWidth <= 768 ? 7 : 5.5;
  const zoomStep = 0.1;

  const getInitialZoom = useCallback(() => {
    const savedZoom = sessionStorage.getItem(ZOOM_KEY);
    const defaultZoom = window.innerWidth <= 768 ? 2.1 : 3.5;
    if (!savedZoom) return defaultZoom;
    const parsed = parseFloat(savedZoom);
    return Number.isFinite(parsed) ? parsed : defaultZoom;
  }, []);

  const getInitialPosition = useCallback(() => {
    const savedPosition = sessionStorage.getItem(POSITION_KEY);
    if (!savedPosition) return { x: 0, y: 0 };

    try {
      const parsed = JSON.parse(savedPosition);
      if (
        parsed &&
        typeof parsed.x === "number" &&
        typeof parsed.y === "number"
      ) {
        return parsed;
      }
    } catch (err) {
      console.error("Invalid saved position:", err);
    }

    return { x: 0, y: 0 };
  }, []);

  const [zoom, setZoom] = useState(getInitialZoom);
  const [position, setPosition] = useState(getInitialPosition);

  const displayWidth = baseSize.width * zoom;
  const displayHeight = baseSize.height * zoom;

  const statusOptions = useMemo(() => {
    return ["ALL", "Available", "Booked", "Sold", "Blocked"];
  }, []);

  const facingOptions = useMemo(() => {
    const values = Object.values(plotMetaMap)
      .map((p) => normalizeFacing(p.facing))
      .filter(Boolean);

    return ["ALL", ...Array.from(new Set(values))];
  }, [plotMetaMap]);

  const selectedPlot = useMemo(() => {
    return plotCoordinates?.find((plot) => plot.id === selectedPlotId) || null;
  }, [selectedPlotId]);

  const hoveredPlot = useMemo(() => {
    return plotCoordinates?.find((plot) => plot.id === hoveredPlotId) || null;
  }, [hoveredPlotId]);

  const isClickOnUI = useCallback((target) => {
    if (!target) return false;

    return Boolean(
      target.closest?.(".top-map-toolbar") ||
        target.closest?.(".legend-panel") ||
        target.closest?.(".zoom-controls") ||
        target.closest?.("select") ||
        target.closest?.("button") ||
        target.closest?.("input") ||
        target.closest?.("[data-ui='true']")
    );
  }, []);

  useEffect(() => {
    targetZoomRef.current = zoom;
    targetPositionRef.current = position;
  }, []); // eslint-disable-line

  useEffect(() => {
    sessionStorage.setItem(COLOR_MODE_KEY, colorMode);
  }, [colorMode]);

  useEffect(() => {
    sessionStorage.setItem(STATUS_FILTER_KEY, statusFilter);
  }, [statusFilter]);

  useEffect(() => {
    sessionStorage.setItem(FACING_FILTER_KEY, facingFilter);
  }, [facingFilter]);

  const persistZoom = useCallback((nextZoom) => {
    sessionStorage.setItem(ZOOM_KEY, String(nextZoom));
  }, []);

  const persistPosition = useCallback((nextPosition) => {
    sessionStorage.setItem(POSITION_KEY, JSON.stringify(nextPosition));
  }, []);

  const getLabelStyle = useCallback(() => {
    if (windowWidth <= 768) {
      return {
        position: "absolute",
        fontSize: "11px",
        fontFamily: "'Montserrat', sans-serif",
        fontWeight: "700",
        padding: "4px 6px",
        textAlign: "center",
        zIndex: 1000,
        lineHeight: 1.2,
        whiteSpace: "nowrap",
        WebkitFontSmoothing: "antialiased",
      };
    }

    if (windowWidth <= 1024) {
      return {
        position: "absolute",
        fontSize: "12px",
        fontFamily: "'Montserrat', sans-serif",
        fontWeight: "700",
        padding: "5px 8px",
        textAlign: "center",
        zIndex: 1000,
        lineHeight: 1.2,
        whiteSpace: "nowrap",
        WebkitFontSmoothing: "antialiased",
      };
    }

    return {
      position: "absolute",
      fontSize: "9px",
      fontFamily: "'Montserrat', sans-serif",
      fontWeight: "700",
      padding: "6px 10px",
      textAlign: "center",
      zIndex: 1000,
      lineHeight: 1.2,
      whiteSpace: "nowrap",
      WebkitFontSmoothing: "antialiased",
    };
  }, [windowWidth]);

  const updateBaseSize = useCallback(() => {
    const container = containerRef.current;
    const { width: naturalWidth, height: naturalHeight } = imageDimensions;

    if (!container || !naturalWidth || !naturalHeight) return;

    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;

    const imageAspect = naturalWidth / naturalHeight;
    const containerAspect = containerWidth / containerHeight;

    let fittedWidth;
    let fittedHeight;

    if (imageAspect > containerAspect) {
      fittedWidth = containerWidth;
      fittedHeight = containerWidth / imageAspect;
    } else {
      fittedHeight = containerHeight;
      fittedWidth = containerHeight * imageAspect;
    }

    setBaseSize({
      width: fittedWidth,
      height: fittedHeight,
    });
  }, [imageDimensions]);

  const calculateBoundaries = useCallback(
    (targetZoom) => {
      const container = containerRef.current;

      if (!container || !baseSize.width || !baseSize.height) {
        return { minX: 0, maxX: 0, minY: 0, maxY: 0 };
      }

      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight;

      const imgWidth = baseSize.width * targetZoom;
      const imgHeight = baseSize.height * targetZoom;

      let minX = 0;
      let maxX = 0;
      let minY = 0;
      let maxY = 0;

      if (imgWidth > containerWidth) {
        const overflowX = (imgWidth - containerWidth) / 2;
        minX = -(overflowX - EDGE_SAFE_PAD);
        maxX = overflowX - EDGE_SAFE_PAD;
      }

      if (imgHeight > containerHeight) {
        const overflowY = (imgHeight - containerHeight) / 2;
        minY = -(overflowY - EDGE_SAFE_PAD);
        maxY = overflowY - EDGE_SAFE_PAD;
      }

      if (minX > maxX) minX = maxX = 0;
      if (minY > maxY) minY = maxY = 0;

      return { minX, maxX, minY, maxY };
    },
    [baseSize]
  );

  const clampPosition = useCallback(
    (targetZoom, targetPosition) => {
      const { minX, maxX, minY, maxY } = calculateBoundaries(targetZoom);

      return {
        x: Math.min(maxX, Math.max(minX, targetPosition.x)),
        y: Math.min(maxY, Math.max(minY, targetPosition.y)),
      };
    },
    [calculateBoundaries]
  );

  const animateToTarget = useCallback(() => {
    animationFrameRef.current = null;

    setZoom((currentZoom) => {
      const targetZoom = targetZoomRef.current;
      const zoomDiff = targetZoom - currentZoom;

      const nextZoom =
        Math.abs(zoomDiff) < 0.001
          ? targetZoom
          : currentZoom + zoomDiff * 0.16;

      persistZoom(Number(nextZoom.toFixed(4)));

      setPosition((currentPosition) => {
        const desiredTargetPos = clampPosition(nextZoom, targetPositionRef.current);

        const dx = desiredTargetPos.x - currentPosition.x;
        const dy = desiredTargetPos.y - currentPosition.y;

        const nextPosition = {
          x: Math.abs(dx) < 0.15 ? desiredTargetPos.x : currentPosition.x + dx * 0.18,
          y: Math.abs(dy) < 0.15 ? desiredTargetPos.y : currentPosition.y + dy * 0.18,
        };

        const rounded = {
          x: Number(nextPosition.x.toFixed(2)),
          y: Number(nextPosition.y.toFixed(2)),
        };

        persistPosition(rounded);
        return rounded;
      });

      const zoomDone = Math.abs(targetZoomRef.current - nextZoom) < 0.001;

      if (!zoomDone) {
        animationFrameRef.current = requestAnimationFrame(animateToTarget);
      }

      return nextZoom;
    });
  }, [clampPosition, persistPosition, persistZoom]);

  const startAnimation = useCallback(() => {
    if (animationFrameRef.current) return;
    animationFrameRef.current = requestAnimationFrame(animateToTarget);
  }, [animateToTarget]);

  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const img = new Image();
    img.src = image;

    img.onload = () => {
      setImageDimensions({
        width: img.naturalWidth,
        height: img.naturalHeight,
      });
      setIsImageLoaded(true);
    };

    img.onerror = () => {
      console.error("Image failed to load");
      setIsImageLoaded(true);
      setError("Failed to load map image");
    };
  }, []);

  useEffect(() => {
    if (!imageDimensions.width || !imageDimensions.height) return;
    updateBaseSize();
  }, [imageDimensions, updateBaseSize]);

  useEffect(() => {
    if (!baseSize.width || !baseSize.height) return;

    const next = clampPosition(zoom, position);
    setPosition(next);
    targetPositionRef.current = next;
    persistPosition(next);
  }, [baseSize, clampPosition]); // eslint-disable-line

  // Derive available units, news and plot metadata (status/facing) from the
  // plots prop passed down by App.jsx.
  useEffect(() => {
    if (!plots) {
      setAvailableUnits("Error");
      setNewsItems(["Error fetching news"]);
      setPlotMetaMap({});
      return;
    }

    const availableCount = plots.filter(
      (entry) => normalizeStatus(entry.Status || entry.status) === "available"
    ).length;
    setAvailableUnits(availableCount);

    const validNews = plots
      .map((row) => (row.news && typeof row.news === "string" ? row.news.trim() : ""))
      .filter((newsItem) => newsItem !== "");
    setNewsItems(validNews.length > 0 ? validNews : ["No news available"]);

    const metaMap = {};
    plots.forEach((plot) => {
      const plotId = plot.projectId || plot.id;
      if (!plotId) return;

      metaMap[plotId] = {
        id: plotId,
        plotNumber: plot.plotNumber || plot.PlotNo || plot.plotNo || "",
        status: plot.Status || plot.status || "Unknown",
        facing: plot.facing || plot.Facing || "TBD",
        blockName: plot.blockName || plot.Block || "",
      };
    });
    setPlotMetaMap(metaMap);
  }, [plots]);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      updateBaseSize();

      const nextZoom = Math.min(maxZoom, Math.max(minZoom, targetZoomRef.current));
      targetZoomRef.current = nextZoom;
      targetPositionRef.current = clampPosition(nextZoom, targetPositionRef.current);
      startAnimation();
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [clampPosition, maxZoom, minZoom, startAnimation, updateBaseSize]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleWheel = (e) => {
      if (isClickOnUI(e.target)) return;

      e.preventDefault();

      const delta = e.deltaY > 0 ? -zoomStep : zoomStep;
      const nextZoom = Math.min(maxZoom, Math.max(minZoom, targetZoomRef.current + delta));

      targetZoomRef.current = nextZoom;
      targetPositionRef.current = clampPosition(nextZoom, targetPositionRef.current);
      startAnimation();
    };

    container.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      container.removeEventListener("wheel", handleWheel);
    };
  }, [clampPosition, maxZoom, minZoom, startAnimation, isClickOnUI]);

  const getStatusColor = useCallback((status) => {
    const key = normalizeStatus(status);
    return STATUS_COLORS[key] || STATUS_COLORS.unknown;
  }, []);

  const getFacingColor = useCallback((facing) => {
    const key = normalizeFacing(facing);
    return FACING_COLORS[key] || FACING_COLORS.TBD;
  }, []);

  const getPlotFilterFill = useCallback(
    (plotId) => {
      const meta = plotMetaMap[plotId];
      if (!meta) return null;

      if (colorMode === "status") {
        const plotStatus = normalizeStatus(meta.status);
        const selectedStatus = normalizeStatus(statusFilter);

        if (statusFilter !== "ALL" && plotStatus !== selectedStatus) {
          return "rgba(255,255,255,0.04)";
        }

        return getStatusColor(meta.status);
      }

      if (colorMode === "facing") {
        const plotFacing = normalizeFacing(meta.facing);
        const selectedFacing =
          facingFilter === "ALL" ? "ALL" : normalizeFacing(facingFilter);

        if (selectedFacing !== "ALL" && plotFacing !== selectedFacing) {
          return "rgba(255,255,255,0.04)";
        }

        return getFacingColor(meta.facing);
      }

      return null;
    },
    [plotMetaMap, colorMode, statusFilter, facingFilter, getStatusColor, getFacingColor]
  );

  const legendItems = useMemo(() => {
    if (colorMode === "status") {
      return [
        { label: "Available", color: STATUS_COLORS.available },
        { label: "Booked", color: STATUS_COLORS.booked },
        { label: "Sold", color: STATUS_COLORS.sold },
        { label: "Soft-Blocked", color: STATUS_COLORS.blocked },
      ];
    }

    if (colorMode === "facing") {
      return [
        { label: "East", color: FACING_COLORS.East },
        { label: "West", color: FACING_COLORS.West },
        { label: "North", color: FACING_COLORS.North },
        { label: "South", color: FACING_COLORS.South },
        { label: "East+South", color: FACING_COLORS["East+South"] },
        { label: "East+North", color: FACING_COLORS["East+North"] },
        { label: "West+South", color: FACING_COLORS["West+South"] },
        { label: "West+North", color: FACING_COLORS["West+North"] },
      ];
    }

    return [];
  }, [colorMode]);

  const handleMouseDown = (e) => {
    if (isClickOnUI(e.target)) return;

    e.preventDefault();
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    });
    setDragDistance(0);
    setWasDragged(false);
  };

  const handleMouseMove = useCallback(
    (e) => {
      if (isClickOnUI(e.target)) return;
      if (!isDragging) return;

      const nextPos = clampPosition(zoom, {
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });

      targetPositionRef.current = nextPos;
      setPosition(nextPos);
      persistPosition(nextPos);

      const distance = Math.hypot(
        e.clientX - (dragStart.x + position.x),
        e.clientY - (dragStart.y + position.y)
      );

      setDragDistance(distance);

      if (distance >= DRAG_THRESHOLD) {
        setWasDragged(true);
      }
    },
    [isDragging, dragStart, position, zoom, clampPosition, persistPosition, isClickOnUI]
  );

  const handleMouseUp = (e) => {
    if (isClickOnUI(e.target)) {
      setIsDragging(false);
      return;
    }

    setIsDragging(false);

    if (dragDistance >= DRAG_THRESHOLD) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const handleTouchStart = (e) => {
    if (isClickOnUI(e.target)) return;

    if (e.touches.length === 1) {
      const touch = e.touches[0];
      setIsDragging(true);
      setDragStart({
        x: touch.clientX - position.x,
        y: touch.clientY - position.y,
      });
      setDragDistance(0);
      setWasDragged(false);
    } else if (e.touches.length === 2) {
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.hypot(
        touch1.clientX - touch2.clientX,
        touch1.clientY - touch2.clientY
      );
      setTouchDistance(distance);
      setIsDragging(false);
    }
  };

  const handleTouchMove = useCallback(
    (e) => {
      if (isClickOnUI(e.target)) return;

      if (e.touches.length === 1 && isDragging) {
        const touch = e.touches[0];

        const nextPos = clampPosition(zoom, {
          x: touch.clientX - dragStart.x,
          y: touch.clientY - dragStart.y,
        });

        targetPositionRef.current = nextPos;
        setPosition(nextPos);
        persistPosition(nextPos);

        const distance = Math.hypot(
          touch.clientX - (dragStart.x + position.x),
          touch.clientY - (dragStart.y + position.y)
        );

        setDragDistance(distance);

        if (distance >= DRAG_THRESHOLD) {
          setWasDragged(true);
        }

        e.preventDefault();
      } else if (e.touches.length === 2) {
        const touch1 = e.touches[0];
        const touch2 = e.touches[1];
        const newDistance = Math.hypot(
          touch1.clientX - touch2.clientX,
          touch1.clientY - touch2.clientY
        );

        if (touchDistance) {
          const zoomFactor = newDistance / touchDistance;

          const nextZoom = Math.min(
            maxZoom,
            Math.max(minZoom, targetZoomRef.current * zoomFactor)
          );

          targetZoomRef.current = nextZoom;
          targetPositionRef.current = clampPosition(nextZoom, targetPositionRef.current);
          startAnimation();
          setTouchDistance(newDistance);
        }

        e.preventDefault();
      }
    },
    [
      isDragging,
      dragStart,
      touchDistance,
      zoom,
      minZoom,
      maxZoom,
      clampPosition,
      persistPosition,
      position,
      startAnimation,
      isClickOnUI,
    ]
  );

  const handleTouchEnd = (e) => {
    if (isClickOnUI(e.target)) {
      setIsDragging(false);
      return;
    }

    if (touchDistance !== null) setTouchDistance(null);
    setIsDragging(false);
  };

  const getLogoWidth = () => {
    if (windowWidth <= 480) return "110px";
    if (windowWidth <= 768) return "140px";
    if (windowWidth <= 1024) return "150px";
    return "130px";
  };

  const zoomIn = () => {
    const nextZoom = Math.min(maxZoom, targetZoomRef.current + zoomStep);
    targetZoomRef.current = nextZoom;
    targetPositionRef.current = clampPosition(nextZoom, targetPositionRef.current);
    startAnimation();
  };

  const zoomOut = () => {
    const nextZoom = Math.max(minZoom, targetZoomRef.current - zoomStep);
    targetZoomRef.current = nextZoom;
    targetPositionRef.current = clampPosition(nextZoom, targetPositionRef.current);
    startAnimation();
  };

  const getResponsivePosition = useCallback(
    (baseX, baseY, offsetX = 0, offsetY = 0) => {
      if (!imageDimensions.width || !imageDimensions.height) {
        return {
          left: `${baseX + offsetX}px`,
          top: `${baseY + offsetY}px`,
        };
      }

      const scaleX = displayWidth / imageDimensions.width;
      const scaleY = displayHeight / imageDimensions.height;

      return {
        left: `${(baseX + offsetX) * scaleX}px`,
        top: `${(baseY + offsetY) * scaleY}px`,
      };
    },
    [displayWidth, displayHeight, imageDimensions]
  );

  const handlePlotSelect = useCallback(
    (plotId) => {
      if (wasDragged || dragDistance >= DRAG_THRESHOLD) return;
      setSelectedPlotId(plotId);
      navigate(`/project/${plotId}`);
    },
    [navigate, wasDragged, dragDistance]
  );

  const handleContextMenu = (e) => {
    e.preventDefault();
  };

  const labelStyle = getLabelStyle();
  const infoTabWidth = windowWidth <= 768 ? "150px" : "190px";

  const newsTickerText =
    newsItems.length > 0 ? newsItems.join(" • ") : "No news available";
  const duplicatedNewsText = `${newsTickerText} • ${newsTickerText}`;

  if (!isImageLoaded) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          width: "100%",
          backgroundColor: "#fff",
          fontFamily: "'Montserrat', sans-serif",
          fontSize: "18px",
          fontWeight: "500",
          color: "black",
        }}
      >
        <img src={Logo} alt="Logo" className="loading-logo" />
        <div className="loader"></div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="map-container"
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onContextMenu={handleContextMenu}
      style={{
        overflow: "hidden",
        position: "relative",
        cursor: isDragging ? "grabbing" : "grab",
        touchAction: "none",
        WebkitTapHighlightColor: "transparent",
        width: "100%",
        height: "100vh",
        backgroundColor: "#ffffff",
      }}
    >
      {error && (
        <div
          data-ui="true"
          style={{
            position: "fixed",
            top: "10px",
            left: "50%",
            transform: "translateX(-50%)",
            backgroundColor: "#ffe6e6",
            color: "red",
            padding: "10px",
            borderRadius: "4px",
            zIndex: 2000,
            fontFamily: "'Montserrat', sans-serif",
            fontSize: "14px",
          }}
        >
          {error}
        </div>
      )}

      <div
        className="top-map-toolbar"
        data-ui="true"
        style={{
          position: "fixed",
          top: "10px",
          left: "10px",
          zIndex: 1600,
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: windowWidth <= 768 ? "6px" : "8px",
          padding: "5px",
          borderRadius: "14px",
          background: "rgba(255,255,255,0.88)",
          backdropFilter: "blur(10px)",
          WebkitBackdropFilter: "blur(10px)",
          boxShadow: "0 8px 22px rgba(0,0,0,0.16)",
          border: "1px solid rgba(0,0,0,0.08)",
          maxWidth: windowWidth <= 768 ? "calc(100% - 20px)" : "unset",
        }}
      >
        <select
          value={colorMode}
          onChange={(e) => {
            const value = e.target.value;
            setColorMode(value);

            if (value !== "status") setStatusFilter("ALL");
            if (value !== "facing") setFacingFilter("ALL");
          }}
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          style={{
            width: windowWidth <= 768 ? "100px" : "124px",
            height: "34px",
            padding: "0 10px",
            borderRadius: "10px",
            border: "1px solid #d1d5db",
            outline: "none",
            background: "rgba(255,255,255,0.96)",
            fontSize: "11px",
            fontFamily: "'Montserrat', sans-serif",
            cursor: "pointer",
            color: "#111827",
          }}
        >
          <option value="none">Select Filter</option>
          <option value="status">Status</option>
          <option value="facing">Facing</option>
        </select>

        {colorMode === "status" && (
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            style={{
              width: windowWidth <= 768 ? "110px" : "112px",
              height: "34px",
              padding: "0 10px",
              borderRadius: "10px",
              border: "1px solid #d1d5db",
              outline: "none",
              background: "rgba(255,255,255,0.96)",
              fontSize: "11px",
              fontFamily: "'Montserrat', sans-serif",
              cursor: "pointer",
              color: "#111827",
            }}
          >
            {statusOptions.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
        )}

        {colorMode === "facing" && (
          <select
            value={facingFilter}
            onChange={(e) => setFacingFilter(e.target.value)}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            style={{
              width: windowWidth <= 768 ? "120px" : "128px",
              height: "34px",
              padding: "0 10px",
              borderRadius: "10px",
              border: "1px solid #d1d5db",
              outline: "none",
              background: "rgba(255,255,255,0.96)",
              fontSize: "11px",
              fontFamily: "'Montserrat', sans-serif",
              cursor: "pointer",
              color: "#111827",
            }}
          >
            {facingOptions.map((facing) => (
              <option key={facing} value={facing}>
                {facing}
              </option>
            ))}
          </select>
        )}
      </div>

      {colorMode !== "none" && (
        <div
          className="legend-panel"
          data-ui="true"
          style={{
            position: "fixed",
            top: "50%",
            left: "10px",
            transform: "translateY(-50%)",
            zIndex: 1600,
            width: windowWidth <= 768 ? "84px" : "100px",
            maxHeight: windowWidth <= 768 ? "48vh" : "56vh",
            overflowY: "auto",
            background: "rgba(255,255,255,0.94)",
            borderRadius: "14px",
            padding: windowWidth <= 768 ? "8px" : "10px",
            boxShadow: "0 8px 20px rgba(0,0,0,0.18)",
            fontFamily: "'Montserrat', sans-serif",
            border: "1px solid rgba(0,0,0,0.08)",
          }}
        >
          <div
            style={{
              fontSize: windowWidth <= 768 ? "11px" : "12px",
              fontWeight: 700,
              marginBottom: "8px",
              color: "#1f2937",
            }}
          >
            Legend
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr",
              gap: "6px",
            }}
          >
            {legendItems.map((item) => (
              <div
                key={item.label}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "7px",
                  fontSize: windowWidth <= 768 ? "8px" : "9px",
                  color: "#111827",
                  lineHeight: 1.2,
                }}
              >
                <span
                  style={{
                    width: "12px",
                    height: "12px",
                    borderRadius: "3px",
                    background: item.color,
                    border: "1px solid rgba(0,0,0,0.15)",
                    display: "inline-block",
                    flexShrink: 0,
                  }}
                />
                <span>{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div
        style={{
          position: "absolute",
          inset: 0,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: `translate(${position.x}px, ${position.y}px)`,
            WebkitTransform: `translate(${position.x}px, ${position.y}px)`,
            transformOrigin: "center center",
          }}
        >
          <div
            style={{
              position: "relative",
              width: `${displayWidth}px`,
              height: `${displayHeight}px`,
              transform: "translate(-50%, -50%)",
              WebkitTransform: "translate(-50%, -50%)",
              overflow: "visible",
            }}
          >
            <img
              src={image}
              alt="Project Map"
              draggable={false}
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                maxWidth: "none",
                maxHeight: "none",
                userSelect: "none",
                display: "block",
                pointerEvents: "none",
              }}
            />

            {imageDimensions.width > 0 && imageDimensions.height > 0 && (
              <svg
                viewBox={`0 0 ${imageDimensions.width} ${imageDimensions.height}`}
                preserveAspectRatio="none"
                style={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  pointerEvents: "none",
                  zIndex: 20,
                  overflow: "visible",
                }}
              >
                {plotCoordinates?.map((plot) => {
                  const fill = getPlotFilterFill(plot.id);
                  if (!fill) return null;

                  return (
                    <polygon
                      key={`filter-${plot.id}`}
                      points={coordsToSvgPoints(plot.coords)}
                      fill={fill}
                      stroke="rgba(255,255,255,0.18)"
                      strokeWidth="0.25"
                      strokeLinejoin="round"
                      vectorEffect="non-scaling-stroke"
                    />
                  );
                })}

                {!selectedPlot && hoveredPlot && (
                  <polygon
                    points={coordsToSvgPoints(hoveredPlot.coords)}
                    fill="rgba(77, 255, 0, 0.57)"
                    stroke="#ffffff"
                    strokeWidth="0.45"
                    strokeLinejoin="round"
                    vectorEffect="non-scaling-stroke"
                  />
                )}

                {selectedPlot && (
                  <polygon
                    points={coordsToSvgPoints(selectedPlot.coords)}
                    fill="rgba(77, 255, 0, 0.60)"
                    stroke="#ffffff"
                    strokeWidth="0.55"
                    strokeLinejoin="round"
                    vectorEffect="non-scaling-stroke"
                  />
                )}
              </svg>
            )}

            <MapAreas
              imageDimensions={imageDimensions}
              displayWidth={displayWidth}
              displayHeight={displayHeight}
              hoveredPlotId={hoveredPlotId}
              setHoveredPlotId={setHoveredPlotId}
              onPlotClick={handlePlotSelect}
            />

            <LocationLabels
              labelStyle={labelStyle}
              getResponsivePosition={getResponsivePosition}
              coords={coords}
              hoveredPlotId={hoveredPlotId}
              onC5Click={() => setShowInfoTabC5(true)}
              onC7Click={() => setShowInfoTabC7(true)}
            />

            {showInfoTabC5 && (
              <div
                className="map-info-card"
                data-ui="true"
                style={{
                  position: "absolute",
                  ...getResponsivePosition(coords.c5.x, coords.c5.y, 0, 0),
                  transform: "translate(-50%, -100%)",
                  backgroundColor: "#024837",
                  padding: windowWidth <= 768 ? "8px" : "10px",
                  borderRadius: "12px",
                  boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
                  zIndex: 2500,
                  width: infoTabWidth,
                  textAlign: "center",
                  pointerEvents: "auto",
                }}
              >
                <h2
                  style={{
                    fontFamily: "'Montserrat', sans-serif",
                    fontSize: windowWidth <= 768 ? "12px" : "14px",
                    fontWeight: "700",
                    margin: "0 0 6px",
                    color: "white",
                  }}
                >
                  Recreation Zone
                </h2>

                <img
                  src={parkImage}
                  alt="Recreation Zone"
                  style={{
                    width: "100%",
                    height: "auto",
                    borderRadius: "5px",
                    display: "block",
                  }}
                />

                <p
                  style={{
                    fontFamily: "'Montserrat', sans-serif",
                    fontSize: windowWidth <= 768 ? "10px" : "12px",
                    lineHeight: 1.4,
                    margin: "6px 0",
                    color: "white",
                  }}
                >
                  Nature and leisure unite in our serene farmland recreation zone.
                </p>

                <button
                  onClick={() => setShowInfoTabC5(false)}
                  style={{
                    padding: windowWidth <= 768 ? "6px 10px" : "7px 12px",
                    fontFamily: "'Montserrat', sans-serif",
                    backgroundColor: "white",
                    color: "black",
                    border: "none",
                    margin: "0px",
                    borderRadius: "5px",
                    cursor: "pointer",
                    fontSize: windowWidth <= 768 ? "10px" : "12px",
                    pointerEvents: "auto",
                  }}
                >
                  Close
                </button>
              </div>
            )}

            {showInfoTabC7 && (
              <div
                className="map-info-card"
                data-ui="true"
                style={{
                  position: "absolute",
                  ...getResponsivePosition(coords.c7.x, coords.c7.y, 0, 0),
                  transform: "translate(-50%, -100%)",
                  backgroundColor: "#024837",
                  padding: windowWidth <= 768 ? "8px" : "10px",
                  borderRadius: "12px",
                  boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
                  zIndex: 2500,
                  width: infoTabWidth,
                  textAlign: "center",
                  pointerEvents: "auto",
                }}
              >
                <h2
                  style={{
                    fontFamily: "'Montserrat', sans-serif",
                    fontSize: windowWidth <= 768 ? "12px" : "14px",
                    fontWeight: "700",
                    margin: "0 0 6px",
                    color: "white",
                  }}
                >
                  Leisure Zone
                </h2>

                <img
                  src={parkImage2}
                  alt="Leisure Zone"
                  style={{
                    width: "100%",
                    height: "auto",
                    borderRadius: "5px",
                    display: "block",
                  }}
                />

                <p
                  style={{
                    fontFamily: "'Montserrat', sans-serif",
                    fontSize: windowWidth <= 768 ? "10px" : "12px",
                    lineHeight: 1.4,
                    margin: "6px 0",
                    color: "white",
                  }}
                >
                  A vibrant space dedicated to relaxation, recreation, and refined community living.
                </p>

                <button
                  onClick={() => setShowInfoTabC7(false)}
                  style={{
                    padding: windowWidth <= 768 ? "6px 10px" : "7px 12px",
                    fontFamily: "'Montserrat', sans-serif",
                    backgroundColor: "white",
                    color: "black",
                    border: "none",
                    margin: "0px",
                    borderRadius: "5px",
                    cursor: "pointer",
                    fontSize: windowWidth <= 768 ? "10px" : "12px",
                    pointerEvents: "auto",
                  }}
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="zoom-controls" data-ui="true">
        <button type="button" onClick={zoomIn}>+</button>
        <button type="button" onClick={zoomOut}>-</button>
      </div>

      <img
        src={logoRight}
        alt="Right Logo"
        data-ui="true"
        style={{
          position: "fixed",
          top: "10px",
          right: "10px",
          width: getLogoWidth(),
          height: "auto",
          zIndex: 1500,
          borderRadius: windowWidth <= 768 ? "16px" : "16px",
          pointerEvents: "none",
          boxShadow: "0px 6px 10px rgba(0, 0, 0, 0.9)",
        }}
      />

      <div
        data-ui="true"
        style={{
          position: "fixed",
          top: `calc(10px + ${getLogoWidth()})`,
          right: "10px",
          fontFamily: "'Montserrat', sans-serif",
          fontSize: windowWidth <= 768 ? "10px" : "12px",
          color: "black",
          padding: "10px 10px",
          backgroundColor: "rgba(255, 255, 255, 0.7)",
          boxShadow: "0 4px 10px rgba(0, 0, 0, 0.2)",
          borderRadius: "16px",
          zIndex: 1500,
          textAlign: "center",
          width: "130px",
          height: "40px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        <span>
          <strong>Available Units:</strong> {availableUnits}
        </span>

        <div
          style={{
            width: "100%",
            overflow: "hidden",
            whiteSpace: "nowrap",
            marginTop: "3px",
            height: "20px",
            color: "black",
            borderRadius: "5px",
          }}
        >
          <span
            style={{
              display: "inline-block",
              fontSize: windowWidth <= 768 ? "10px" : "12px",
              fontWeight: "600",
              animation: `scroll ${
                newsItems.length > 0 && newsItems[0] !== "Error fetching news" ? 30 : 5
              }s linear infinite`,
            }}
          >
            {duplicatedNewsText}
          </span>
        </div>
      </div>

      <img
        src={contactBtn}
        alt="Contact"
        data-ui="true"
        style={{
          position: "fixed",
          bottom: windowWidth <= 768 ? "10px" : "10px",
          right: "10px",
          width: windowWidth <= 768 ? "100px" : "100px",
          height: "auto",
          zIndex: 1500,
          pointerEvents: "auto",
          borderRadius: windowWidth <= 768 ? "20px" : "28px",
          boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.6)",
          cursor: "pointer",
        }}
        onClick={() => (window.location.href = "https://www.hasirufarms.com/contact")}
      />

      <img
        src={compass}
        alt="compass"
        data-ui="true"
        style={{
          position: "fixed",
          bottom: windowWidth <= 768 ? "10px" : "10px",
          backgroundColor: "rgba(255, 255, 255, 0.7)",
          padding: "10px",
          left: "10px",
          width: windowWidth <= 768 ? "60px" : "60px",
          height: "auto",
          zIndex: 1500,
          pointerEvents: "auto",
          borderRadius: windowWidth <= 768 ? "16px" : "16px",
          boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.6)",
          cursor: "pointer",
        }}
      />

      <div
        data-ui="true"
        style={{
          position: "fixed",
          bottom: windowWidth <= 768 ? "10px" : "10px",
          left: "50%",
          transform: "translateX(-50%)",
          fontFamily: "'Montserrat', sans-serif",
          fontSize: windowWidth <= 768 ? "8px" : "9px",
          color: "white",
          padding: "5px 10px",
          borderRadius: "5px",
          width: "200px",
          textAlign: "center",
          zIndex: 1500,
          pointerEvents: "none",
        }}
      >
        <strong>Plot Map</strong> <br /> Powered by <br /> Luminexa Technologies
      </div>

      <style jsx>{`
        @keyframes scroll {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }
      `}</style>
    </div>
  );
};

export default Map;
